import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import useStorageState from 'use-storage-state';
import authService from '../services/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessToken, setAccessToken, deleteAccessToken] = useStorageState('accessToken', {defaultValue: null, serializer: { stringify: v => v, parse: v => v }});
  const authCheckRef = useRef(false);

  // Initialize auth status on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          setAuthenticated(false);
          setUser(null);
          setLoading(false);
          return;
        }

        // Try to get user profile
        const profile = await authService.getProfile();
        setUser(profile);
        setAuthenticated(true);
        setRequires2FA(false);
      } catch (error) {
        // Token might be expired or invalid
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            // Try to refresh the token
            const response = await authService.refresh(refreshToken);
            setAccessToken(response.accessToken);
            setUser(response.user);
            setAuthenticated(true);
            setRequires2FA(false);
          } catch (refreshError) {
            // Refresh failed, clear everything
            deleteAccessToken();
            localStorage.removeItem('refreshToken');
            setAuthenticated(false);
            setUser(null);
          }
        } else {
          setAuthenticated(false);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    if (!authCheckRef.current) {
      authCheckRef.current = true;
      initAuth();
    }
  }, [setAccessToken, deleteAccessToken]);

  const checkAuthStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      // Try to get user profile
      const profile = await authService.getProfile();
      setUser(profile);
      setAuthenticated(true);
      setRequires2FA(false);
    } catch (error) {
      // Token might be expired or invalid
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          // Try to refresh the token
          const response = await authService.refresh(refreshToken);
          setAccessToken(response.accessToken);
          setUser(response.user);
          setAuthenticated(true);
          setRequires2FA(false);
        } catch (refreshError) {
          // Refresh failed, clear everything
          deleteAccessToken();
          localStorage.removeItem('refreshToken');
          setAuthenticated(false);
          setUser(null);
        }
      } else {
        setAuthenticated(false);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [setAccessToken, deleteAccessToken]);

  useEffect(() => {
    if (accessToken) {
      const tokenParts = accessToken.split('.');
      if (tokenParts.length >= 2) {
        const payload = JSON.parse(atob(tokenParts[1]));
        setIsAdmin(payload.roles.includes('admin'));
      }
    }
  }, [accessToken]);

  const login = async (email, password) => {
    const response = await authService.login(email, password);
    
    if (response.requires2FA) {
      setRequires2FA(true);
      setTempToken(response.tempToken);
      setUser(response.user);
      return response;
    }

    if (response.force2FASetup) {
      // User needs to setup 2FA
      // Store tokens so they can complete setup
      setAccessToken(response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      setUser(response.user);
      setAuthenticated(true);
      return response;
    }

    // Store tokens
    setAccessToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    
    setAuthenticated(true);
    setRequires2FA(false);
    setUser(response.user);
    
    return response;
  };

  const verify2FA = async (token) => {
    const response = await authService.verify2FA(token, tempToken);
    
    // Store tokens
    setAccessToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    
    setAuthenticated(true);
    setRequires2FA(false);
    setTempToken(null);
    setUser(response.user);
    
    return response;
  };

  const enable2FA = async (token, secret) => {
    const response = await authService.enable2FA(token, secret);
    
    // Update tokens
    setAccessToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    
    setUser(response.user);
    
    return response;
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    deleteAccessToken();
    localStorage.removeItem('refreshToken');
    setAuthenticated(false);
    setUser(null);
    setRequires2FA(false);
    setTempToken(null);
  };

  const value = {
    user,
    authenticated,
    requires2FA,
    loading,
    login,
    verify2FA,
    enable2FA,
    logout,
    checkAuthStatus,
    isAdmin,
    accessToken,
    setAccessToken,
    deleteAccessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
